var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useSelector } from 'react-redux';
import { FourOhFour } from '../ui/views/404/404';
import { Loader } from 'src/ui/components/Loader/Loader';
import { useInitKeycloak } from 'src/shared/hooks/useInitKeycloak';
import { coreActions } from 'src/redux/core/coreActions';
import { E_ROUTE_KEY, routes } from './routes';
import { KeycloakEntity } from 'src/shared/KeycloakEntity';
import store from 'src/shared/store';
import { getAutoPlayPermission, getIsCasesViewAllowed, getPermissions } from 'src/redux/auth/authSelectors';
import { E_PERMISSION_KEY } from 'src/services/auth/types/permissionsTypes';
import { authActions } from 'src/redux/auth/authActions';
import { useRouter } from 'src/shared/hooks/useRouting';
import { URL_PATH } from 'src/shared/constants';
var ProtectedRoute = function (props) {
    var isAuth = props.isAuth, name = props.name, children = props.children;
    var permissions = useSelector(getPermissions);
    var isCasesViewAllowed = useSelector(getIsCasesViewAllowed);
    var isAutoPlayAllowed = useSelector(getAutoPlayPermission);
    var router = useRouter();
    var _a = useState(_jsx(Loader, {})), render = _a[0], setRender = _a[1];
    useEffect(function () {
        if (!isAuth) {
            KeycloakEntity.kc.login(); // enough to redirect on login page with right params
        }
    }, [isAuth]);
    useEffect(function () {
        if (permissions) {
            switch (name) {
                case E_ROUTE_KEY.USERS: {
                    var allowed = (permissions[E_PERMISSION_KEY.USER_MANAGEMENT_VIEW] || {}).allowed;
                    if (allowed) {
                        setRender(children);
                    }
                    break;
                }
                case E_ROUTE_KEY.CASES: {
                    if (isCasesViewAllowed) {
                        setRender(children);
                    }
                    else {
                        if (isAutoPlayAllowed) {
                            router.replace(URL_PATH.AUTO_SEARCH);
                        }
                        else {
                            setRender(_jsx(FourOhFour, {}));
                        }
                    }
                    break;
                }
                case E_ROUTE_KEY.AUTO_SEARCH: {
                    if (isAutoPlayAllowed) {
                        setRender(children);
                    }
                    else {
                        setRender(_jsx(FourOhFour, {}));
                    }
                    break;
                }
                default: {
                    setRender(children);
                    break;
                }
            }
        }
    }, [permissions, name, children]);
    if (!render) {
        return _jsx(FourOhFour, {});
    }
    return render;
};
export var createRoute = function (realm, isAuth, isInit) {
    var defaultRoute = {
        routesNotProtected: {},
        routesProtected: {},
    };
    var _a = Object.entries(routes).reduce(function (acc, _a) {
        var _b, _c;
        var key = _a[0], route = _a[1];
        if (!route.protected) {
            return __assign(__assign({}, acc), { routesNotProtected: __assign(__assign({}, acc.routesNotProtected), (_b = {}, _b[key] = route, _b)) });
        }
        else {
            return __assign(__assign({}, acc), { routesProtected: __assign(__assign({}, acc.routesProtected), (_c = {}, _c[key] = route, _c)) });
        }
    }, defaultRoute), routesNotProtected = _a.routesNotProtected, routesProtected = _a.routesProtected;
    var notProtectedRoutes = Object.values(routesNotProtected).map(function (route) { return _jsx(Route, __assign({}, route), route.path); });
    var protectedRoutes = realm
        ? Object.entries(routesProtected).map(function (_a) {
            var key = _a[0], route = _a[1];
            return (_jsx(Route, __assign({}, route, { element: _jsx(ProtectedRoute, __assign({ realm: realm, name: key, isAuth: isAuth, isInit: isInit }, { children: route.element })) }), route.path));
        })
        : [];
    return __spreadArray(__spreadArray([], notProtectedRoutes, true), protectedRoutes, true);
};
export var Routing = function (_a) {
    var _b;
    var urlConfig = _a.urlConfig;
    var _c = useInitKeycloak(urlConfig === null || urlConfig === void 0 ? void 0 : urlConfig.realm), isAuth = _c.isAuth, isInit = _c.isInit;
    var routes = createRoute(urlConfig === null || urlConfig === void 0 ? void 0 : urlConfig.realm, isAuth, isInit);
    if (isAuth) {
        var keycloakLocale = (_b = KeycloakEntity === null || KeycloakEntity === void 0 ? void 0 : KeycloakEntity.userInfo) === null || _b === void 0 ? void 0 : _b.locale;
        if (keycloakLocale) {
            store.dispatch(coreActions.updateLocale(keycloakLocale));
        }
        store.dispatch(coreActions.fetchConfRequested());
        store.dispatch(authActions.fetchGroupsRequested());
        store.dispatch(authActions.fetchPermissionsRequested());
    }
    var basePathEnhanced = (urlConfig === null || urlConfig === void 0 ? void 0 : urlConfig.basePath) ? "".concat(urlConfig.basePath) : '';
    if (routes.length < 1 || !isInit) {
        return (_jsx("div", __assign({ style: { display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' } }, { children: _jsx(Loader, {}) })));
    }
    return (_jsxs(Routes, { children: [_jsxs(Route, __assign({ path: "".concat(basePathEnhanced, "/:realm/") }, { children: [routes, _jsx(Route, { index: true, element: _jsx(Navigate, { to: "".concat(basePathEnhanced, "/").concat(urlConfig.realm, "/cases") }) })] })), _jsx(Route, { path: '*', element: _jsx(FourOhFour, {}) })] }));
};
